import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const CHANNELS = ['EMAIL', 'WHATSAPP'];
const PROVIDER_OPTIONS = {
  EMAIL: [
    { value: 'GENERIC_WEBHOOK', label: 'Webhook generico' },
  ],
  WHATSAPP: [
    { value: 'GENERIC_WEBHOOK', label: 'Webhook/API oficial (recomendado)' },
    { value: 'WHATSAPP_WEB_EXPERIMENTAL', label: 'WhatsApp Web Experimental (BYOC)' },
  ],
};

const emptyChannelConfig = (canal) => ({
  canal,
  provider_name: 'GENERIC_WEBHOOK',
  endpoint_url: '',
  auth_header_name: '',
  auth_header_value: '',
  sender_identity: '',
  active: false,
  sandbox_mode: true,
  timeout_ms: 10000,
  config_json: '{}',
});

const channelTitle = (canal) => (canal === 'EMAIL' ? 'E-mail' : 'WhatsApp');

function safeParseJson(text) {
  const raw = String(text || '').trim();
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    return {};
  } catch (_) {
    return null;
  }
}

function toJsonText(value) {
  try {
    return JSON.stringify(value || {}, null, 2);
  } catch (_) {
    return '{}';
  }
}

const CollectionsChannelsPage = () => {
  const [configs, setConfigs] = useState({
    EMAIL: emptyChannelConfig('EMAIL'),
    WHATSAPP: emptyChannelConfig('WHATSAPP'),
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState('');

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/collections/settings/channels');
      const items = Array.isArray(data?.items) ? data.items : [];
      const next = {
        EMAIL: emptyChannelConfig('EMAIL'),
        WHATSAPP: emptyChannelConfig('WHATSAPP'),
      };

      CHANNELS.forEach((canal) => {
        const list = items.filter((item) => item.canal === canal);
        const chosen = list.find((item) => item.active) || list[0];
        if (chosen) {
          next[canal] = {
            canal,
            provider_name: chosen.provider_name || 'GENERIC_WEBHOOK',
            endpoint_url: chosen.endpoint_url || '',
            auth_header_name: chosen.auth_header_name || '',
            auth_header_value: chosen.auth_header_value || '',
            sender_identity: chosen.sender_identity || '',
            active: chosen.active === true,
            sandbox_mode: chosen.sandbox_mode !== false,
            timeout_ms: Number(chosen.timeout_ms || 10000),
            config_json: toJsonText(chosen.config_json || {}),
          };
        }
      });

      setConfigs(next);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar configuracoes de canal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const setChannelConfig = (canal, patch) => {
    setConfigs((old) => ({
      ...old,
      [canal]: {
        ...old[canal],
        ...patch,
      },
    }));
  };

  const getChannelConfigJson = (canal) => {
    const parsed = safeParseJson(configs[canal]?.config_json);
    return parsed && typeof parsed === 'object' ? parsed : {};
  };

  const setChannelConfigJsonKey = (canal, key, value) => {
    const config = getChannelConfigJson(canal);
    setChannelConfig(canal, {
      config_json: toJsonText({
        ...config,
        [key]: value,
      }),
    });
  };

  const setProviderName = (canal, providerName) => {
    const config = getChannelConfigJson(canal);
    const nextConfig = { ...config };

    if (canal === 'WHATSAPP' && providerName === 'WHATSAPP_WEB_EXPERIMENTAL') {
      if (nextConfig.experimental_enabled == null) nextConfig.experimental_enabled = false;
      if (nextConfig.risk_acknowledged == null) nextConfig.risk_acknowledged = false;
      if (!nextConfig.risk_ack_version) nextConfig.risk_ack_version = 'v1';
      if (nextConfig.client_assumes_block_risk == null) nextConfig.client_assumes_block_risk = true;
      if (!nextConfig.session_name) nextConfig.session_name = '';
    }

    setChannelConfig(canal, {
      provider_name: providerName,
      config_json: toJsonText(nextConfig),
    });
  };

  const saveChannel = async (canal) => {
    const current = configs[canal];
    setSaving(canal);
    try {
      let configJson = {};
      try {
        configJson = current.config_json ? JSON.parse(current.config_json) : {};
      } catch (_) {
        toast.error(`config_json de ${channelTitle(canal)} invalido`);
        return;
      }

      if (canal === 'WHATSAPP' && current.provider_name === 'WHATSAPP_WEB_EXPERIMENTAL') {
        if (configJson.risk_acknowledged !== true) {
          toast.error('Aceite de risco obrigatorio para usar WhatsApp Web Experimental');
          return;
        }
        if (current.active && configJson.experimental_enabled !== true) {
          toast.error('Para ativar o canal, habilite o modo experimental');
          return;
        }
        configJson = {
          ...configJson,
          risk_mode: 'CLIENT_ASSUMED',
          risk_ack_version: String(configJson.risk_ack_version || 'v1'),
          risk_acknowledged: true,
          client_assumes_block_risk: true,
        };
      }

      await axios.put(`/api/collections/settings/channels/${canal}`, {
        canal,
        provider_name: current.provider_name,
        endpoint_url: current.endpoint_url || null,
        auth_header_name: current.auth_header_name || null,
        auth_header_value: current.auth_header_value || null,
        sender_identity: current.sender_identity || null,
        active: current.active,
        sandbox_mode: current.sandbox_mode,
        timeout_ms: Number(current.timeout_ms || 10000),
        config_json: configJson,
      });
      toast.success(`Canal ${channelTitle(canal)} atualizado`);
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || `Erro ao salvar configuracao de ${channelTitle(canal)}`);
    } finally {
      setSaving('');
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <section className="rounded-2xl border border-cyan-300 bg-gradient-to-r from-slate-900 via-cyan-900 to-blue-900 p-6 text-white shadow-soft">
        <h1 className="text-3xl font-bold">Canais de Disparo</h1>
        <p className="mt-2 text-cyan-100">
          Configure conectores por empresa para envio real de mensagens da regua de cobranca.
        </p>
      </section>

      {loading && (
        <div className="flex justify-center py-4">
          <div className="loader h-8 w-8" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {CHANNELS.map((canal) => {
          const current = configs[canal];
          const configJson = getChannelConfigJson(canal);
          const isWhatsappExperimental = canal === 'WHATSAPP' && current.provider_name === 'WHATSAPP_WEB_EXPERIMENTAL';
          const riskAccepted = configJson.risk_acknowledged === true;
          const experimentalEnabled = configJson.experimental_enabled === true;
          const riskAcceptedAt = String(configJson.risk_acknowledged_at || '').trim();
          const riskAcceptedBy = String(configJson.risk_acknowledged_by || '').trim();

          return (
            <div key={canal} className="card">
              <div className="card-header flex items-center justify-between">
                <span>{channelTitle(canal)}</span>
                <span className={current.active ? 'badge-success' : 'badge-danger'}>{current.active ? 'Ativo' : 'Inativo'}</span>
              </div>
              <div className="card-body space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <select className="form-select" value={current.provider_name} onChange={(e) => setProviderName(canal, e.target.value)}>
                    {(PROVIDER_OPTIONS[canal] || []).map((provider) => (
                      <option key={provider.value} value={provider.value}>
                        {provider.label}
                      </option>
                    ))}
                  </select>
                  <input
                    className="form-input"
                    type="number"
                    min="1000"
                    max="60000"
                    placeholder="Timeout (ms)"
                    value={current.timeout_ms}
                    onChange={(e) => setChannelConfig(canal, { timeout_ms: e.target.value })}
                  />
                </div>

                <input
                  className="form-input"
                  placeholder={canal === 'EMAIL' ? 'Endpoint URL do conector de e-mail' : 'Endpoint URL do conector WhatsApp'}
                  value={current.endpoint_url}
                  onChange={(e) => setChannelConfig(canal, { endpoint_url: e.target.value })}
                />

                {isWhatsappExperimental && (
                  <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 space-y-2">
                    <p className="text-sm font-semibold text-rose-800">Modo Experimental: WhatsApp Web (BYOC)</p>
                    <p className="text-xs text-rose-700">
                      Este modo usa tecnologia similar ao WhatsApp Web no conector externo do cliente.
                      O risco de bloqueio do numero e de indisponibilidade operacional fica por conta da empresa cliente.
                    </p>
                    <label className="inline-flex items-center gap-2 text-sm text-rose-800">
                      <input
                        type="checkbox"
                        checked={riskAccepted}
                        onChange={(e) => setChannelConfigJsonKey(canal, 'risk_acknowledged', e.target.checked)}
                      />
                      Eu aceito e assumo os riscos deste modo para esta empresa
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-rose-800">
                      <input
                        type="checkbox"
                        checked={experimentalEnabled}
                        onChange={(e) => setChannelConfigJsonKey(canal, 'experimental_enabled', e.target.checked)}
                      />
                      Habilitar disparo real em modo experimental
                    </label>
                    <input
                      className="form-input"
                      placeholder="session_name no conector (opcional)"
                      value={String(configJson.session_name || '')}
                      onChange={(e) => setChannelConfigJsonKey(canal, 'session_name', e.target.value)}
                    />
                    {(riskAcceptedAt || riskAcceptedBy) && (
                      <p className="text-xs text-rose-700">
                        Aceite registrado em {riskAcceptedAt || '-'} por {riskAcceptedBy || '-'}
                      </p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    className="form-input"
                    placeholder="Nome do header auth"
                    value={current.auth_header_name}
                    onChange={(e) => setChannelConfig(canal, { auth_header_name: e.target.value })}
                  />
                  <input
                    className="form-input"
                    placeholder="Valor do header auth"
                    value={current.auth_header_value}
                    onChange={(e) => setChannelConfig(canal, { auth_header_value: e.target.value })}
                  />
                </div>

                <input
                  className="form-input"
                  placeholder={canal === 'EMAIL' ? 'Remetente (nome/email)' : 'Remetente (numero/ID)'}
                  value={current.sender_identity}
                  onChange={(e) => setChannelConfig(canal, { sender_identity: e.target.value })}
                />

                <textarea
                  className="form-textarea"
                  rows={4}
                  placeholder="config_json (headers extras, flags do provider, etc)"
                  value={current.config_json}
                  onChange={(e) => setChannelConfig(canal, { config_json: e.target.value })}
                />

                <div className="flex flex-wrap gap-4">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(current.active)}
                      onChange={(e) => setChannelConfig(canal, { active: e.target.checked })}
                    />
                    Canal ativo
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(current.sandbox_mode)}
                      onChange={(e) => setChannelConfig(canal, { sandbox_mode: e.target.checked })}
                    />
                    Sandbox mode
                  </label>
                </div>

                <button className="btn-primary" onClick={() => saveChannel(canal)} disabled={saving === canal}>
                  {saving === canal ? 'Salvando...' : `Salvar ${channelTitle(canal)}`}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CollectionsChannelsPage;
